import { Form, Formik, FormikHelpers, useFormikContext } from 'formik';
import { useEffect } from 'react';
import api, { ApiResponse, isAxiosErrorHandled } from '../../../../api';
import Button from '../../../../components/button/Button';
import { endpoints } from '../../../../endpoints.config';
import {
    EMPTY_PAYEE_VALUES,
    EMPTY_TRANSMITTER_INFO,
    PayeeForm,
    PayeeFormState,
} from '../EditPayee';
import { PaymentStepProps, useFetchPayeeCurrencies } from './MakePayment';
import { CheckboxInput } from '../../../../components/form/CheckboxInput';
import { toCamelCase } from '../../../../helpers/formatFormFieldNames';
import { getErrorMessage } from '../../../../errors';
import { Toast } from '../../../../helpers/toast';
import { useGetFiatAccountDetails } from 'helpers/useGetFiatAccountDetails';
import { useIsFinancialInstitution, usePayeeValidationSchema } from '../helpers';
import { SelectOption } from 'models/formSingleSelectField';
import { convertLocaleDateToUtcDate } from '../../../../helpers/convertLocaleDateToUtcDate';
import { useTranslation } from 'react-i18next';

export type ErrorCountry = { name: string; countryISO3: string; errorCode: string | null };
type Props = PaymentStepProps & {
    initialValues: Partial<PayeeFormState & { bSavePayee: boolean; id?: number }>;
    hideSaveOption?: boolean;
    availableCountries: {
        label: string;
        value: string;
        states: SelectOption[] | null;
    }[];
    errorCountries: ErrorCountry[];
    btntext?: string;
};

// Component to normalize form values in real-time
const NormalizeFormFields: React.FC<{ normalizeSpecialChars: (str: string) => string }> = ({ normalizeSpecialChars }) => {
    const { values, setFieldValue } = useFormikContext<PayeeFormState & { bSavePayee: boolean }>();
    
    // Fields that need normalization (all string fields from EMPTY_PAYEE_VALUES)
    const fieldsToNormalize: (keyof PayeeFormState)[] = [
        'accountName',
        'name',
        'payeesReference',
        'routingNumber',
        'swiftNumber',
        'intermediaryBic',
        'accountNumber',
        'iban',
        'bankName',
        'addressLine1',
        'addressLine2',
        'townCity',
        'state',
        'postCode',
    ];

    // Normalize values in real-time as they change
    useEffect(() => {
        fieldsToNormalize.forEach((fieldName) => {
            const currentValue = values[fieldName];
            if (currentValue && typeof currentValue === 'string') {
                const normalized = normalizeSpecialChars(currentValue);
                if (normalized !== currentValue) {
                    setFieldValue(fieldName, normalized, false);
                    
                    // If this field is a dropdown (FormSingleSelectField), also update the input element
                    // React-select uses inputId to set the id of the input element
                    // Use setTimeout to ensure DOM is ready
                    setTimeout(() => {
                        const inputElement = document.getElementById(fieldName.toString()) as HTMLInputElement;
                        if (inputElement) {
                            // Update the input value directly
                            const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
                                window.HTMLInputElement.prototype,
                                'value'
                            )?.set;
                            if (nativeInputValueSetter) {
                                nativeInputValueSetter.call(inputElement, normalized);
                                // Trigger input event to notify react-select
                                const inputEvent = new Event('input', { bubbles: true });
                                inputElement.dispatchEvent(inputEvent);
                            } else {
                                // Fallback: direct assignment
                                inputElement.value = normalized;
                                const inputEvent = new Event('input', { bubbles: true });
                                inputElement.dispatchEvent(inputEvent);
                            }
                        }
                    }, 0);
                }
            }
        });


        if (values.transmitter) {
            // All string fields from EMPTY_TRANSMITTER_INFO
            const transmitterFields: (keyof typeof values.transmitter)[] = [
                'name',
                'accountNumber',
                'addressLine1',
                'addressLine2',
                'townCity',
                'state',
                'postcode',
            ];
            transmitterFields.forEach((fieldName) => {
                const currentValue = values.transmitter?.[fieldName];
                if (currentValue && typeof currentValue === 'string') {
                    const normalized = normalizeSpecialChars(currentValue);
                    if (normalized !== currentValue) {
                        setFieldValue(`transmitter.${fieldName}`, normalized, false);
                    }
                }
            });
        }
    }, [
        values.accountName, values.name, values.payeesReference, values.routingNumber,
        values.swiftNumber, values.intermediaryBic, values.accountNumber, values.iban,
        values.bankName, values.addressLine1, values.addressLine2, values.townCity,
        values.state, values.postCode,
        values.transmitter?.name, values.transmitter?.accountNumber,
        values.transmitter?.addressLine1, values.transmitter?.addressLine2,
        values.transmitter?.townCity, values.transmitter?.state, values.transmitter?.postcode,
        setFieldValue, normalizeSpecialChars
    ]);

    return null;
};

export const Recipient: React.FC<Props> = ({
    nextStep,
    initialValues,
    hideSaveOption,
    availableCountries,
    errorCountries,
    btntext,
}) => {
    const { t } = useTranslation('common');
    const selectedAccountDetails = useGetFiatAccountDetails();
    const bFinancialInstitution = useIsFinancialInstitution();

    const onSubmit = async (
        values: PayeeFormState & { bSavePayee: boolean },
        helpers: FormikHelpers<PayeeFormState & { bSavePayee: boolean }>
    ) => {
        try {
            if (!selectedAccountDetails?.id) {
                Toast.openErrorToast('Unable to setup payee: account details are still loading. Please try again in a moment.');
                return;
            }

            let endpoint = endpoints.accounts.createPayee;

            if (initialValues.id) {
                endpoint = endpoints.accounts.editPayee;
            }

            const { postCode, ...payload } = values;

            const res = await api.post<
                ApiResponse<{ allowedTransferTypes: string; bInternational: boolean; id: number }>
            >(endpoint, {
                ...payload,
                postcode: postCode,
                bHide: !values.bSavePayee,
                currencyCode: values.currencyCode,
                customerAssetAccountsId: selectedAccountDetails?.id,
                transmitter: payload.transmitter
                    ? {
                        ...payload.transmitter,
                        dateOfBirth: convertLocaleDateToUtcDate(payload.transmitter.dateOfBirth),
                    }
                    : null,
            });

            if (res.data.status === '1') {
                nextStep({
                    ...values,
                    id: res.data.details.id,
                    allowedTransferTypes: res.data.details.allowedTransferTypes,
                    bInternational: res.data.details.bInternational,
                });
            } else {
                throw new Error();
            }
        } catch (err) {
            if (isAxiosErrorHandled(err)) {
                let matchedField = false;
                err.response.data.errors.forEach((error) => {
                    const fieldName = toCamelCase(error.fieldName) as string;
                    if (Object.keys(validationSchema.fields).includes(fieldName)) {
                        matchedField = true;
                        helpers.setFieldError(fieldName, getErrorMessage(error.messageCode));
                    }
                });
                if (!matchedField) {
                    Toast.openErrorToast('Unable to setup payee');
                }
            } else {
                Toast.openErrorToast('Unable to setup payee');
            }
        }
    };

    const validationSchema = usePayeeValidationSchema(errorCountries);
    const accountDetails = useGetFiatAccountDetails();

    const { availableCurrencies } = useFetchPayeeCurrencies();

    const transmitterInitialInfo = bFinancialInstitution ? EMPTY_TRANSMITTER_INFO : null;
    const charMap = {
        // A variants
        'á': 'a', 'à': 'a', 'â': 'a', 'ä': 'a', 'ã': 'a', 'å': 'a',
        'ā': 'a', 'ą': 'a', 'æ': 'ae', 'Á': 'A', 'À': 'A', 'Â': 'A',
        'Ä': 'A', 'Ã': 'A', 'Å': 'A', 'Ā': 'A', 'Ą': 'A', 'Æ': 'AE',

        // C variants
        'ç': 'c', 'č': 'c', 'Ç': 'C', 'Č': 'C',

        // D variants
        'ď': 'd', 'Ď': 'D',

        // E variants
        'é': 'e', 'è': 'e', 'ê': 'e', 'ë': 'e', 'ē': 'e', 'ę': 'e',
        'ě': 'e', 'É': 'E', 'È': 'E', 'Ê': 'E', 'Ë': 'E', 'Ē': 'E',
        'Ę': 'E', 'Ě': 'E',

        // G variants
        'ǧ': 'g', 'Ǧ': 'G',

        // H variants
        'ḥ': 'h', 'Ḥ': 'H',

        // I variants
        'í': 'i', 'ì': 'i', 'î': 'i', 'ï': 'i', 'ī': 'i', 'į': 'i',
        'Í': 'I', 'Ì': 'I', 'Î': 'I', 'Ï': 'I', 'Ī': 'I', 'Į': 'I',

        // J variants
        'ǰ': 'j', 'J̌': 'J',

        // L variants
        'ł': 'l', 'Ł': 'L',

        // N variants
        'ñ': 'n', 'ň': 'n', 'Ñ': 'N', 'Ň': 'N',

        // O variants
        'ó': 'o', 'ò': 'o', 'ô': 'o', 'ö': 'o', 'õ': 'o', 'ø': 'o',
        'ō': 'o', 'ő': 'o', 'ǫ': 'o', 'œ': 'oe', 'Ó': 'O', 'Ò': 'O',
        'Ô': 'O', 'Ö': 'O', 'Õ': 'O', 'Ø': 'O', 'Ō': 'O', 'Ő': 'O',
        'Ǫ': 'O', 'Œ': 'OE',

        // R variants
        'ř': 'r', 'Ř': 'R',

        // S variants
        'š': 's', 'ß': 'ss', 'Š': 'S',

        // T variants
        'ť': 't', 'Ť': 'T',

        // U variants
        'ú': 'u', 'ù': 'u', 'û': 'u', 'ü': 'u', 'ū': 'u', 'ű': 'u',
        'ų': 'u', 'Ú': 'U', 'Ù': 'U', 'Û': 'U', 'Ü': 'U', 'Ū': 'U',
        'Ű': 'U', 'Ų': 'U',

        // Y variants
        'ý': 'y', 'Ý': 'Y',

        // Z variants
        'ż': 'z', 'ž': 'z', 'Ż': 'Z', 'Ž': 'Z'
    };
    // Function to normalize special characters
    const normalizeSpecialChars = (str: string) => {
        if (!str) return str;

        return str.split('').map((char: string) => charMap[char as keyof typeof charMap] || char).join('');
    };
    return (
        <Formik
            initialValues={{
                ...EMPTY_PAYEE_VALUES,
                bFinancialInstitution,
                transmitter: transmitterInitialInfo,
                bSavePayee: true,
                ...initialValues,
            }}
            validationSchema={validationSchema}
            onSubmit={onSubmit}
        >
            {({ isSubmitting }) => (
                <Form>
                    <NormalizeFormFields normalizeSpecialChars={normalizeSpecialChars} />
                    <PayeeForm
                        productType={accountDetails?.productDisplayName}
                        availableCountries={availableCountries}
                        availableCurrencies={availableCurrencies}
                        sourceAccountCurrencyCode={selectedAccountDetails?.currencyCode}
                    />
                    {!hideSaveOption && (
                        <CheckboxInput
                            withFormStyling
                            name="bSavePayee"
                            label="Save recipient details to move money faster next time"
                        />
                    )}
                    <Button disabled={isSubmitting || !selectedAccountDetails?.id} type="submit" color="third">
                        {btntext ?? t('next')}
                    </Button>
                </Form>
            )}
        </Formik>
    );
};
